import React, { Fragment } from 'react';
import classnames from 'classnames';
import { compose, withProps } from 'recompose';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import { metaPropTypes } from 'config/propTypes';
import statePageContent from 'config/statePageContent';
import stateSelector from 'store/selectors/state';
import { withErrorHandler, withRouteHandler } from 'decorators';
import { Page, Container, Section } from 'components/core/Page';
import { H2 } from 'components/core/Typography';
import { LinkFarm } from 'components/core/LinkFarm';
import { Accordion } from 'components/search/Accordion';
import { StateSearchHero } from 'components/landing/StateSearchHero';
import { StateNavLinks } from 'components/landing/StateNavLinks';
import { StatePageIntro } from 'components/landing/StatePageIntro';
import { StatePageFacts } from 'components/landing/StatePageFacts';
import { StatePageReasonsToMove } from 'components/landing/StatePageReasonsToMove';
import { StatePageEconomicOutlook } from 'components/landing/StatePageEconomicOutlook';
import { StatePageLargestEmployers } from 'components/landing/StatePageLargestEmployers';
import { StatePagePlacesToLive } from 'components/landing/StatePagePlacesToLive';
import { withPageViewTracking } from 'components/core/library/tracking-utils';

import './StatePage.scss';
import { PAGE_TYPES } from '@sparefoot/tracking-events';

export const enhance = compose(
	connect(stateSelector),
	withErrorHandler,
	withRouteHandler((props) => !props.title),
	withProps(({ location }) => {
		const { state } = location; // overriding the actual state, which would be location.state
		return { content: statePageContent[state] };
	}),
	withPageViewTracking(PAGE_TYPES.STATE_LANDING),
);

export function StatePage({
	trackingPixel,
	meta,
	allCities,
	location,
	stateUrl,
}) {
	return (
		<Page
			className="state-landing-page"
			trackingPixel={trackingPixel}
			pageType={PAGE_TYPES.STATE_LANDING}
			title={meta.title}
			description={meta.description}
			canonical={meta.canonical}
		>
			<StateSearchHero
				location={location}
				stateUrl={stateUrl}
				stateNickname={statePageContent[location.state].stateNickname}
			/>
			{!get(statePageContent[location.state], 'isDC') && (
				<Fragment>
					<StateNavLinks location={location} />
					<StatePageIntro
						state={location.fullState}
						welcome={statePageContent[location.state].welcome}
					/>
					<Section id="self-storage">
						<StatePageFacts
							state={location.fullState}
							selfStorageFacts={
								statePageContent[location.state]
									.selfStorageFacts
							}
							cityPriceList={
								statePageContent[location.state].cityPriceList
							}
						/>
					</Section>
					<Section id="reasons">
						<StatePageReasonsToMove
							state={location.fullState}
							reasonsToMove={
								statePageContent[location.state].reasonsToMove
							}
							movingTo={statePageContent[location.state].movingTo}
						/>
					</Section>
					<Section id="economic-outlook">
						<StatePageEconomicOutlook
							state={location.fullState}
							economicOutlook={
								statePageContent[location.state].economicOutlook
							}
						/>
					</Section>
					<StatePageLargestEmployers
						state={location.fullState}
						largestEmployers={
							statePageContent[location.state].largestEmployers
						}
					/>
					<Section id="places">
						<StatePagePlacesToLive
							state={location.fullState}
							placesToLive={
								statePageContent[location.state].placesToLive
							}
						/>
					</Section>
					<Container
						className="resources-container"
						id="resources"
					>
						<Accordion
							accordionTitle={`Moving to ${location.fullState} Resources`}
							content={get(
								statePageContent[location.state],
								'movingToResources',
							)}
						/>
					</Container>
				</Fragment>
			)}

			<Container
				className={classnames({
					'is-DC': get(statePageContent[location.state], 'isDC'),
				})}
			>
				{allCities.length && (
					<Fragment>
						<Section>
							<H2
								size="large"
								className="all-cities"
							>
								All {location.fullState} Cities{' '}
							</H2>
						</Section>
						<Section>
							{' '}
							{/* Requires this div for Expand to work properly */}
							<LinkFarm
								links={allCities}
								columns={4}
								primary
								size="medium"
							/>
						</Section>
					</Fragment>
				)}
			</Container>
		</Page>
	);
}

StatePage.propTypes = {
	allCities: PropTypes.arrayOf(
		PropTypes.shape({
			city: PropTypes.string,
			state: PropTypes.string,
			uri: PropTypes.string.isRequired,
		}).isRequired,
	).isRequired,
	location: PropTypes.shape({
		state: PropTypes.string.isRequired,
		fullState: PropTypes.string.isRequired,
	}).isRequired,
	stateUrl: PropTypes.string,
	trackingPixel: PropTypes.string.isRequired,
	meta: PropTypes.shape(metaPropTypes),
};

export default enhance(StatePage);
